import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronDown, ChevronUp, Link45deg } from 'react-bootstrap-icons'
import { InfluencerContext } from '../../../../contexts/InfluencerContext'
import { SharedCampaignContext } from '../../../../contexts/Advertiser/SharedCampaignContext'
import { SharedInfluencerContext } from '../../../../contexts/Advertiser/SharedInfluencerContext'
import Authentication from '../../../../models/Authentication'
import PostModel from '../../../../models/Post'
import PostsTimeline from '../../Shared/PostsTimeline'
import Post from '../../Shared/Post'
import axiosService from '../../../../utils/AxiosService'
import { toastError } from '../../../../components/FormComponents/Toasts'
import ReachbirdCollaborations from './ReachbirdCollaborations'

const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export default observer(function Collaborations() {
  const { id, authentication_id, uuid } = useParams()
  const influencerContext = useContext(InfluencerContext)
  const sharedCampaignContext = useContext(SharedCampaignContext)
  const sharedInfluencerContext = useContext(SharedInfluencerContext)

  const isAdvertiserContext = !!sharedCampaignContext?.campaignState
  const isInfluencerShareContext = !!sharedInfluencerContext?.influencerState

  const pmAuthentications = influencerContext?.authentications || []
  const sharedInfluencerAuthentications = sharedInfluencerContext?.authentications || []
  const currentAuthentications = isInfluencerShareContext
    ? sharedInfluencerAuthentications
    : (isAdvertiserContext ? [] : pmAuthentications)

  const currentId = isInfluencerShareContext
    ? (sharedInfluencerAuthentications[0]?.id)
    : (isAdvertiserContext ? authentication_id : id)

  const currentAuthentication = useMemo(
    () =>
      currentAuthentications.find(authentication => +authentication.id === +currentId) ||
      new Authentication(),
    [currentId, currentAuthentications, currentAuthentications?.length || 0]
  )

  const getSponsoredPosts = (authentication) => {
    if (!authentication?.id || authentication.sponsored_posts_loading) return
    authentication.sponsored_posts_loading = true

    const url = isInfluencerShareContext && uuid
      ? `/api/advertiser/influencer_share/authentications/${uuid}/posts/sponsored`
      : `/api/project_manager/authentications/${authentication.id}/posts/sponsored`

    axiosService.get(url)
      .then(posts => {
        authentication.sponsored_posts = (posts || []).map(post => new PostModel(post))
        authentication.sponsored_posts_loaded = true
      })
      .catch(({ data: { error } }) => {
        toastError(error || 'Failed to load sponsored posts')
        authentication.sponsored_posts = []
      })
      .finally(() => {
        authentication.sponsored_posts_loading = false
      })
  }

  useEffect(() => {
    if (currentAuthentication?.id && !currentAuthentication.sponsored_posts_loaded) {
      getSponsoredPosts(currentAuthentication)
    }
  }, [currentAuthentication?.id, currentAuthentication?.sponsored_posts_loaded])

  useEffect(() => {
    if (currentAuthentication?.id && !currentAuthentication.sponsored_posts_loaded) {
      getSponsoredPosts(currentAuthentication)
    }
  }, [currentAuthentication?.id, currentAuthentication?.sponsored_posts_loaded])

  const [expandedGroups, setExpandedGroups] = useState({})

  const brandGroups = useMemo(() => {
    const posts = currentAuthentication?.sponsored_posts || []
    const groups = {}

    posts.forEach(post => {
      const brands = (post.brands && post.brands.length ? post.brands : [{
        id: `unknown-${post.id}`,
        name: 'Unknown brand',
        domain: '',
        logo_url: null,
      }])

      brands.forEach(brand => {
        const key = brand.uuid || brand.id || brand.name
        if (!groups[key]) {
          groups[key] = {
            key,
            brand,
            posts: [],
          }
        }
        groups[key].posts.push(post)
      })
    })

    return Object.values(groups).sort((a, b) => b.posts.length - a.posts.length)
  }, [currentAuthentication?.sponsored_posts, (currentAuthentication?.sponsored_posts || []).length])

  useEffect(() => {
    if (brandGroups.length && Object.keys(expandedGroups).length === 0) {
      const initial = brandGroups.reduce((acc, group, index) => {
        acc[group.key] = index === 0
        return acc
      }, {})
      setExpandedGroups(initial)
    }
  }, [brandGroups.length])

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const isLoading = currentAuthentication?.sponsored_posts_loading

  // Konvertáljuk a sponsored_posts-ot a PostsTimeline által várható formátumra
  const postsForTimeline = useMemo(() => {
    const posts = (currentAuthentication?.sponsored_posts || []).map(post => {
      // A Post modellből jövő adatok már tartalmaznak authentication és brands mezőket
      // (PostBlueprint-ből jönnek), de biztosítjuk, hogy plain objektumok legyenek
      return {
        id: post.id,
        authentication_id: post.authentication_id,
        posted_at: post.posted_at,
        thumbnail: post.thumbnail,
        text: post.text,
        url: post.url,
        comments_count: post.comments_count,
        likes_count: post.likes_count,
        views_count: post.views_count,
        shares_count: post.shares_count,
        provider: post.provider,
        authentication: post.authentication,
        brands: post.brands || []
      }
    })
    console.log('Collaborations postsForTimeline:', {
      count: posts.length,
      isLoading,
      sponsoredPostsCount: currentAuthentication?.sponsored_posts?.length
    })
    return posts
  }, [currentAuthentication?.sponsored_posts, isLoading])

  return (
    <div className="space-y-6 bg-rb-grey dark:bg-rb-dark-mode-background p-4">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold dark:text-white">Collaborations</h2>
      </div>

      {!isAdvertiserContext && !isInfluencerShareContext && <ReachbirdCollaborations />}

      {/* Timeline komponens brand bontásban */}
      <div className="w-full">
        <PostsTimeline
          posts={postsForTimeline}
          groupBy="brand"
          loading={isLoading}
          showListView={false}
          disableBrandNavigation={isInfluencerShareContext}
        />
      </div>

      {isLoading && (
        <div className="bg-white dark:bg-rb-dark-mode rounded-2xl border border-rb-widget-border dark:border-rb-dark-mode-lighter p-6 text-sm text-rb-lighter-grey dark:text-gray-400">
          Loading sponsored posts…
        </div>
      )}

      {!isLoading && !brandGroups.length && (
        <div className="bg-white dark:bg-rb-dark-mode rounded-2xl border border-dashed border-rb-widget-border dark:border-rb-dark-mode-lighter p-8 text-center text-sm text-rb-lighter-grey dark:text-gray-400">
          No sponsored posts have been recorded for this authentication yet.
        </div>
      )}

      {!isLoading && brandGroups.length > 0 && (
        <div className="space-y-4">
          {brandGroups.map(group => {
            const { brand, posts, key } = group
            const expanded = expandedGroups[key]
            return (
              <div key={key} className="bg-white dark:bg-rb-dark-mode rounded-2xl border border-rb-widget-border dark:border-rb-dark-mode-lighter shadow-sm overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left dark:text-white"
                  onClick={() => toggleGroup(key)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {brand.logo_url ? (
                      <img src={brand.logo_url} alt={brand.name} className="h-12 w-12 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-xl bg-rb-grey dark:bg-rb-dark-mode-lighter flex items-center justify-center text-sm font-semibold text-rb-lighter-grey dark:text-gray-400 flex-shrink-0">
                        {brand.name?.slice(0, 2)?.toUpperCase() || 'BR'}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-lg font-semibold truncate dark:text-white">{brand.name || 'Unknown brand'}</p>
                      <p className="text-xs text-rb-lighter-grey dark:text-gray-400">{brand.domain || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-rb-lighter-grey dark:text-gray-400">{posts.length} sponsored posts</span>
                    {expanded ? <ChevronUp className="dark:text-white" /> : <ChevronDown className="dark:text-white" />}
                  </div>
                </button>
                {expanded && (
                  <div className="border-t border-rb-widget-border dark:border-rb-dark-mode-lighter bg-rb-grey dark:bg-rb-dark-mode-background px-5 py-4">
                    <div className="flex flex-wrap gap-4">
                      {posts.map(post => (
                        <Post
                          key={`${key}-${post.id}`}
                          post={post}
                          reach={currentAuthentication.reach}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
})

